import {buildQuestionsFromSubTableConfig} from 'src/modules/incident/incidentSubTable';
import {buildIsTaskQuestionsFromConfig} from 'src/modules/tarea/isTaskConfig';

export type FormatoQuestionOption = {
  key: string;
  label: string;
  title: string;
  tipoRespuesta?: string;
  subsectionIndex: number;
  nPregunta: string;
  seccionIndex: number;
  subseccionIndex: number;
};

export type WalkFormatoQuestionsOpts = {
  tiposRespuesta?: string[];
  excludeSubsectionPath?: string;
  includeHiddenFromSource?: boolean;
};

const USER_QUESTION_TYPES = ['usuario', 'perfilEmpleado'];

export function parseFormatoSubsectionPath(
  path: string | undefined,
): {seccionIndex: number; subseccionIndex: number} | null {
  const m = String(path || '').match(/(\d+)\.subseccions\.(\d+)\s*$/);
  if (!m) return null;
  return {seccionIndex: Number(m[1]), subseccionIndex: Number(m[2])};
}

function resolveSubsectionQuestions(subseccion: any): any[] {
  if (subseccion?.isTask) {
    return buildIsTaskQuestionsFromConfig(subseccion) || [];
  }
  const subTableQuestions = buildQuestionsFromSubTableConfig(subseccion) || [];
  if (subTableQuestions.length > 0) return subTableQuestions;
  return Array.isArray(subseccion?.preguntas) ? subseccion.preguntas : [];
}

function questionTitle(pregunta: any, nPregunta: string): string {
  return (
    pregunta?.pregunta?.titulo ||
    pregunta?.pregunta?.label ||
    pregunta?.titulo ||
    pregunta?.label ||
    `Pregunta ${nPregunta}`
  );
}

function questionTipo(pregunta: any): string {
  return String(pregunta?.tipoRespuesta ?? pregunta?.pregunta?.tipoRespuesta ?? '');
}

/**
 * Recorre `formato` con el mismo índice que buildBlankProcedureList
 * (slot de sección + subsección + cada pregunta generada o real).
 */
export function collectFormatoQuestionOptions(
  formato: any[] | undefined,
  opts: WalkFormatoQuestionsOpts = {},
): FormatoQuestionOption[] {
  const questions: FormatoQuestionOption[] = [];
  if (!Array.isArray(formato)) return questions;

  const exclude = parseFormatoSubsectionPath(opts.excludeSubsectionPath);
  const tipoFilter = Array.isArray(opts.tiposRespuesta)
    ? new Set(opts.tiposRespuesta.map((t) => String(t)))
    : null;

  let listIndex = 0;
  for (let seccionIndex = 0; seccionIndex < formato.length; seccionIndex++) {
    const seccion = formato[seccionIndex];
    listIndex += 1;
    const subsecciones = Array.isArray(seccion?.subseccions) ? seccion.subseccions : [];

    for (let subseccionIndex = 0; subseccionIndex < subsecciones.length; subseccionIndex++) {
      const subseccion = subsecciones[subseccionIndex];
      const subsectionIndex = listIndex;
      const resolved = resolveSubsectionQuestions(subseccion);
      const skipEmit =
        exclude != null &&
        exclude.seccionIndex === seccionIndex &&
        exclude.subseccionIndex === subseccionIndex;

      listIndex += 1;

      for (let i = 0; i < resolved.length; i++) {
        const pregunta = resolved[i];
        const nPreguntaRaw = pregunta?.nPregunta ?? i + 1;
        const hasNPregunta = typeof nPreguntaRaw === 'number' || typeof nPreguntaRaw === 'string';
        const hidden = Boolean(pregunta?.isTaskExecutorFromSource) && !opts.includeHiddenFromSource;

        if (hasNPregunta && !hidden && !skipEmit) {
          const nPregunta = String(nPreguntaRaw);
          const tipoRespuesta = questionTipo(pregunta);
          if (!tipoFilter || tipoFilter.has(tipoRespuesta)) {
            const title = questionTitle(pregunta, nPregunta);
            const categoria = subseccion?.categoria || '';
            questions.push({
              key: `${subsectionIndex}_${nPregunta}`,
              title,
              label: categoria ? `${categoria} · ${title}` : title,
              tipoRespuesta,
              subsectionIndex,
              nPregunta,
              seccionIndex,
              subseccionIndex,
            });
          }
        }

        listIndex += 1;
      }
    }
  }

  const uniq = new Map<string, FormatoQuestionOption>();
  for (const q of questions) {
    if (!uniq.has(q.key)) uniq.set(q.key, q);
  }
  return Array.from(uniq.values());
}

export function collectFormatoQuestionKeys(formato: any[] | undefined): Set<string> {
  return new Set(collectFormatoQuestionOptions(formato).map((q) => q.key));
}

export function collectLocationQuestionOptions(
  formato: any[] | undefined,
  excludeSubsectionPath?: string,
): {value: string; label: string}[] {
  return collectFormatoQuestionOptions(formato, {
    tiposRespuesta: ['location'],
    excludeSubsectionPath,
  }).map((q) => ({value: q.key, label: q.label}));
}

export function collectUserQuestionOptions(
  formato: any[] | undefined,
  excludeSubsectionPath?: string,
): {value: string; label: string}[] {
  return collectFormatoQuestionOptions(formato, {
    tiposRespuesta: USER_QUESTION_TYPES,
    excludeSubsectionPath,
  }).map((q) => ({value: q.key, label: q.label}));
}
